/**
 * Owner dashboard (app_admins only). For now: the food review queue. Users send new products and
 * corrections with package photos; here the owner compares what the database has now with what was
 * sent, edits anything, and approves (the shared food is published or updated for everyone) or
 * rejects with a note the user sees.
 */
import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { declaredFrom, LABEL_FIELD_DEFS, type LabelField } from '@engine/label';
import { NUTRIENTS, NUTRIENT_BY_KEY, roundNutrient } from '@engine/nutrients';
import { analyzeLabel, type TrueLabelReport } from '@engine/tef';
import { LabelCheckLine } from '../components/Badges';
import { Icon } from '../components/Icon';
import { Card, Empty, Segmented, Skeleton, Spinner } from '../components/ui';
import { formatDateTime } from '../lib/dates';
import { FOOD_SOURCE_LABEL } from '../lib/foodNutrients';
import { errorMessage } from '../lib/logger';
import type { AdminSubmission, NutrientMap, ReviewedFood, SubmissionStatus } from '../lib/types';
import { useApp, useAsync } from '../state/app';

type Filter = SubmissionStatus | 'all';

const LABEL_KEYS: readonly string[] = LABEL_FIELD_DEFS.map((d) => d.key);
const ORIGIN_TEXT: Readonly<Record<string, string>> = { database: 'our database', mine: 'their own saved copy', open_food_facts: 'Open Food Facts' };

const perServing = (per100: number, grams: number): number => (per100 * grams) / 100;
const show = (v: number | undefined): string => (v === undefined ? '—' : String(roundNutrient(v)));
const num = (s: string): number | null => {
  const t: string = s.trim();
  if (t === '') return null;
  const n: number = Number(t);
  return Number.isFinite(n) && n >= 0 ? n : Number.NaN;
};

export function OwnerPage(): ReactElement {
  const { source, isAdmin, refreshReviews } = useApp();
  const [filter, setFilter] = useState<Filter>('pending');
  const [version, setVersion] = useState<number>(0);
  const list = useAsync<AdminSubmission[]>(() => (isAdmin ? source.listSubmissions(filter) : Promise.resolve([])), [source, filter, version, isAdmin]);

  if (!isAdmin) {
    return <Card title="Owner"><Empty title="Owners only">This page is for the site owner's account.</Empty></Card>;
  }
  const rows: AdminSubmission[] = list.data ?? [];
  const decided = (): void => { setVersion((v) => v + 1); refreshReviews(); };

  return (
    <div className="stack">
      <Card title="Food review queue" sub="new products and corrections sent in by users"
        right={<button type="button" className="btn btn-sm" onClick={decided}>Refresh</button>}>
        <div style={{ marginBottom: 14 }}>
          <Segmented<Filter> value={filter} onChange={setFilter}
            options={[{ value: 'pending', label: 'Waiting' }, { value: 'approved', label: 'Approved' }, { value: 'rejected', label: 'Rejected' }, { value: 'all', label: 'All' }]} />
        </div>
        {list.error !== null && <div className="note err">{list.error}</div>}
        {list.loading && list.data === null ? <Skeleton height={240} /> : rows.length === 0 ? (
          <Empty title={filter === 'pending' ? 'Nothing waiting' : 'Nothing here'}>{filter === 'pending' ? 'New products and corrections show up here as users send them.' : ''}</Empty>
        ) : (
          <div className="sub-list">{rows.map((s) => <SubmissionCard key={s.id} s={s} onDecided={decided} />)}</div>
        )}
      </Card>
    </div>
  );
}

function SubmissionCard({ s, onDecided }: { s: AdminSubmission; onDecided: () => void }): ReactElement {
  const { source, toast } = useApp();
  const pending: boolean = s.status === 'pending';
  const t = s.target;
  // What the database (or the product page the user saw) has now, per 100 g.
  const now: NutrientMap = t?.nutrients ?? s.original.nutrients ?? {};
  const nowServing: number | null = t?.portions[0]?.grams ?? s.original.serving_grams ?? null;
  const [name, setName] = useState<string>(s.name);
  const [brand, setBrand] = useState<string>(s.brand ?? '');
  const [barcode, setBarcode] = useState<string>(s.barcode ?? '');
  const [servingLabel, setServingLabel] = useState<string>(s.serving_label ?? t?.portions[0]?.label ?? '');
  const [grams, setGrams] = useState<string>(String(s.serving_grams ?? nowServing ?? 100));
  const [ingredients, setIngredients] = useState<string>(s.ingredients ?? '');
  const [note, setNote] = useState<string>('');
  const [busy, setBusy] = useState<'approve' | 'reject' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [urls, setUrls] = useState<Record<string, string>>({});
  const g: number = Number(grams) > 0 ? Number(grams) : 100;

  // Final values per serving: what was sent, else (for a correction) what is there now.
  const keys: string[] = useMemo(() => {
    const present = new Set<string>([...LABEL_KEYS, ...Object.keys(s.nutrients), ...(s.kind === 'correction' ? Object.keys(now) : [])]);
    return NUTRIENTS.map((n) => n.key).filter((k) => present.has(k));
  }, [s, now]);
  const [final, setFinal] = useState<Record<string, string>>(() => {
    const grams0: number = s.serving_grams ?? nowServing ?? 100;
    const out: Record<string, string> = {};
    for (const k of keys) {
      const v: number | undefined = s.nutrients[k] ?? (s.kind === 'correction' ? now[k] : undefined);
      out[k] = v === undefined ? '' : String(roundNutrient(perServing(v, grams0)));
    }
    return out;
  });

  useEffect(() => {
    let cancelled: boolean = false;
    source.photoUrls(s.photo_paths).then((u) => { if (!cancelled) setUrls(u); }).catch(() => undefined);
    return () => { cancelled = true; };
  }, [source, s.photo_paths]);

  const finalPer100: NutrientMap | string = useMemo(() => {
    const out: NutrientMap = {};
    for (const k of keys) {
      const v: number | null = num(final[k] ?? '');
      if (v === null) continue;
      if (Number.isNaN(v)) return `${NUTRIENT_BY_KEY.get(k)?.name ?? k} must be a number of 0 or more.`;
      out[k] = roundNutrient((v * 100) / g);
    }
    return out;
  }, [final, keys, g]);

  const report: TrueLabelReport | null = useMemo(() => {
    if (typeof finalPer100 === 'string') return null;
    const per: Partial<Record<LabelField, number>> = {};
    for (const d of LABEL_FIELD_DEFS) {
      const v: number | undefined = finalPer100[d.key];
      if (v !== undefined) per[d.field] = perServing(v, g) / d.factor;
    }
    const declared = declaredFrom({ found: true, servingSizeText: null, servingSizeG: g, servingsPerContainer: s.servings_per_container, perServing: per, lessThanOne: [], unreadable: [] });
    if (typeof declared === 'string') return null;
    try { return analyzeLabel(declared); } catch { return null; }
  }, [finalPer100, g, s.servings_per_container]);

  const decide = async (decision: 'approve' | 'reject'): Promise<void> => {
    setError(null);
    let food: ReviewedFood | null = null;
    if (decision === 'approve') {
      if (typeof finalPer100 === 'string') { setError(finalPer100); return; }
      if (Object.keys(finalPer100).length === 0) { setError('Fill in the values from the photos before approving.'); return; }
      if (name.trim() === '') { setError('The food needs a name.'); return; }
      if (barcode.trim() !== '' && !/^\d{8,14}$/.test(barcode.trim())) { setError('The barcode must be 8 to 14 digits.'); return; }
      if (!(Number(grams) > 0 && Number(grams) <= 5000)) { setError('The serving weight must be more than 0 and at most 5000 g.'); return; }
      food = {
        name: name.trim(), brand: brand.trim() || null, barcode: barcode.trim() || null,
        serving_label: servingLabel.trim() || null, serving_grams: Number(grams), ingredients: ingredients.trim() || null, nutrients: finalPer100,
      };
    } else if (note.trim() === '') {
      setError('Say why it was rejected; the user sees this note.');
      return;
    }
    setBusy(decision);
    try {
      await source.reviewSubmission(s.id, decision, note.trim() || null, food);
      toast(decision === 'approve' ? `Approved: ${name.trim()} is in the food database` : 'Rejected');
      onDecided();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  const origin: string = s.kind === 'correction'
    ? t ? `${FOOD_SOURCE_LABEL[t.source] ?? t.source} food #${t.id}` : ORIGIN_TEXT[s.original.source ?? ''] ?? 'the product page'
    : t ? `shared ${FOOD_SOURCE_LABEL[t.source] ?? t.source} food #${t.id} with this barcode` : 'nothing (new product)';

  return (
    <article className="sub-card">
      <div className="sub-head">
        <span className={`chip k-${s.kind}`}>{s.kind === 'correction' ? 'Correction' : 'New product'}</span>
        <span className={`chip s-${s.status}`}>{s.status === 'pending' ? 'waiting' : s.status}</span>
        <h3>{s.name}{s.brand ? <span className="faint" style={{ fontWeight: 600 }}> · {s.brand}</span> : null}</h3>
        {s.barcode && <span className="chip"><Icon name="barcode" size={12} /> {s.barcode}</span>}
        <span className="right">{s.submitter_email ?? s.submitter_id.slice(0, 8)} · {formatDateTime(s.created_at)}</span>
      </div>

      {s.user_note && <div className="note info"><b>User says:</b> {s.user_note}</div>}
      <div className="faint" style={{ fontSize: 12.5, fontWeight: 600 }}>
        Changes: {origin}.{s.label_check ? ` Their True Label check: ${s.label_check}.` : ''}{s.ai_model ? ` Read by ${s.ai_model}.` : ' Typed by hand.'}
        {s.allergens.length > 0 ? ` Allergens: ${s.allergens.join(', ')}.` : ''}
      </div>

      {s.photo_paths.length > 0 ? (
        <div className="sub-photos">
          {s.photo_paths.map((p) => (
            <a key={p} href={urls[p]} target="_blank" rel="noreferrer" title="Open full size">
              {urls[p] ? <img src={urls[p]} alt="Package photo" loading="lazy" /> : <Spinner />}
            </a>
          ))}
        </div>
      ) : <div className="faint" style={{ fontSize: 12.5, fontWeight: 600 }}>No photos were sent.</div>}

      {pending && (
        <div className="form-grid" style={{ gridTemplateColumns: '2fr 1fr 1fr' }}>
          <label className="field"><span>Name</span><input className="input" value={name} maxLength={300} onChange={(e) => setName(e.target.value)} /></label>
          <label className="field"><span>Brand</span><input className="input" value={brand} maxLength={120} onChange={(e) => setBrand(e.target.value)} /></label>
          <label className="field"><span>Barcode</span><input className="input" value={barcode} inputMode="numeric" onChange={(e) => setBarcode(e.target.value)} /></label>
          <label className="field"><span>Serving (as printed)</span><input className="input" value={servingLabel} maxLength={120} onChange={(e) => setServingLabel(e.target.value)} /></label>
          <label className="field"><span>Serving weight (g)</span><input className="input" type="number" min={0} step="any" value={grams} onChange={(e) => setGrams(e.target.value)} /></label>
        </div>
      )}

      <div style={{ overflowX: 'auto' }}>
        <table className="cmp-table">
          <thead>
            <tr>
              <th>Per serving ({g} g)</th>
              <th style={{ textAlign: 'right' }}>{s.kind === 'correction' ? 'Now' : t ? 'In database' : ''}</th>
              <th style={{ textAlign: 'right' }}>Sent</th>
              <th style={{ textAlign: 'right' }}>{pending ? 'Publish' : ''}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {keys.map((k) => {
              const def = NUTRIENT_BY_KEY.get(k);
              const nowV: number | undefined = now[k] !== undefined ? perServing(now[k] as number, g) : undefined;
              const sentV: number | undefined = s.nutrients[k] !== undefined ? perServing(s.nutrients[k] as number, g) : undefined;
              const fin: number | null = num(final[k] ?? '');
              const changed: boolean = pending && fin !== null && !Number.isNaN(fin) && (nowV === undefined || Math.abs(fin - nowV) > Math.max(0.05, Math.abs(nowV) * 0.02));
              return (
                <tr key={k} className={changed ? 'changed' : ''}>
                  <td>{def?.name ?? k}</td>
                  <td className="num">{show(nowV)}</td>
                  <td className="num">{show(sentV)}</td>
                  <td className="num" style={{ width: 110 }}>
                    {pending
                      ? <input className="input" inputMode="decimal" value={final[k] ?? ''} onChange={(e) => setFinal((f) => ({ ...f, [k]: e.target.value }))} aria-label={`${def?.name ?? k} to publish`} />
                      : null}
                  </td>
                  <td className="faint">{def?.unit}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {pending && report && <LabelCheckLine report={report} />}

      {pending && (
        <label className="field"><span>Ingredients</span><textarea className="input" value={ingredients} maxLength={4000} onChange={(e) => setIngredients(e.target.value)} style={{ minHeight: 50 }} /></label>
      )}

      {pending ? (
        <>
          <label className="field"><span>Note to the user (required to reject)</span>
            <input className="input" value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Checked against the photo, thanks!" />
          </label>
          {error !== null && <div className="note err">{error}</div>}
          <div className="row" style={{ justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-danger" disabled={busy !== null} onClick={() => void decide('reject')}>{busy === 'reject' ? <Spinner /> : <Icon name="close" size={16} />} Reject</button>
            <button type="button" className="btn btn-primary" disabled={busy !== null} onClick={() => void decide('approve')}>{busy === 'approve' ? <Spinner /> : <Icon name="check" size={16} />} Approve and publish</button>
          </div>
        </>
      ) : (
        <div className="faint" style={{ fontSize: 12.5, fontWeight: 600 }}>
          {s.status === 'approved' ? `Approved${s.promoted_food_id !== null ? ` as food #${s.promoted_food_id}` : ''}` : 'Rejected'}
          {s.reviewed_at ? ` ${formatDateTime(s.reviewed_at)}` : ''}{s.reviewer_note ? `: “${s.reviewer_note}”` : '.'}
        </div>
      )}
    </article>
  );
}
